import { useState } from 'react';

import { Row } from '@tanstack/react-table';
import { NotificationRule } from '@/features/notification-rules/schemas/notification-rules.schema.ts';
import { Button } from '@/components/ui/button';
import { LoaderIcon, TrashIcon } from 'lucide-react';
import {
  Credenza,
  CredenzaClose,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from '@/components/ui/credenza.tsx';
import { useDeleteNotificationRulesMutation } from '@/features/notification-rules/hooks/mutations/use-delete-notification-rules.ts';

export function NotificationRulesTableToolbarDeleteDialog({
  notificationRules,
  onSuccess,
  showTrigger = true,
}: {
  notificationRules: Row<NotificationRule>['original'][];
  onSuccess?: () => void;
  showTrigger?: boolean;
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { mutate: deleteNotificationRuleMutation, isPending: isDeletePending } =
    useDeleteNotificationRulesMutation({
      onSuccess: () => {
        onSuccess?.();
        setIsDialogOpen(false);
      },
    });

  const onClickDelete = () => {
    deleteNotificationRuleMutation({
      notificationRuleIds: notificationRules.map((n) => n.notificationRuleId),
    });
  };

  return (
    <Credenza open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      {showTrigger ? (
        <CredenzaTrigger asChild>
          <Button variant="outline" size="sm" className="flex w-min gap-2">
            <TrashIcon className="size-4" aria-hidden="true" />
            <span className="hidden sm:inline-block">Delete ({notificationRules.length})</span>
          </Button>
        </CredenzaTrigger>
      ) : null}
      <CredenzaContent>
        <CredenzaHeader>
          <CredenzaTitle>Confirm Notification Rules Deletion</CredenzaTitle>
          <CredenzaDescription>
            Are you sure you want to delete{' '}
            <span className="font-medium">
              {notificationRules.map((n) => n.notificationTitle).join(', ')}
            </span>
            {notificationRules.length === 1 ? ' notification rule' : ' notification rules'}?
            This action cannot be undone.
          </CredenzaDescription>
        </CredenzaHeader>
        <CredenzaFooter className="gap-2 sm:space-x-0">
          <CredenzaClose asChild>
            <Button variant="outline">Cancel</Button>
          </CredenzaClose>
          <Button
            aria-label="Delete selected rows"
            variant="destructive"
            onClick={onClickDelete}
            disabled={isDeletePending}
          >
            {isDeletePending && (
              <LoaderIcon className="mr-1.5 size-4 animate-spin" aria-hidden="true" />
            )}
            Delete
          </Button>
        </CredenzaFooter>
      </CredenzaContent>
    </Credenza>
  );
}

export default NotificationRulesTableToolbarDeleteDialog;
